import { useState, useEffect } from 'react';
import { ArrowLeft, MoreVertical, Edit2, Save, X, Award, Trophy, Flame, Zap, Medal, Timer, Apple, Activity, ChevronRight, Target, Crown, TrendingUp, AlertCircle, Home, User, Shield, Settings as SettingsIcon, Coins, Sparkles } from 'lucide-react';
import { supabase } from '../lib/supabase';
import NotificationPrompt from '../components/NotificationPrompt';
import OnboardingBanner from '../components/OnboardingBanner';
import CompleteProfileModal from '../components/CompleteProfileModal';
import Settings from './Settings';
import LeaderboardModal from '../components/LeaderboardModal';
import { useDataCache } from '../contexts/DataCacheContext';

type ActivePage = 'home' | 'progress' | 'activity' | 'profile';

interface ProfileProps {
  profile: {
    name: string;
    email: string;
    chronological_age: number | null;
    biological_age: number | null;
    gender: string;
    country: string;
    onboarding_completed: boolean;
    is_admin?: boolean;
    is_pro_member?: boolean;
    subscription_expires_at?: string;
  };
  onBack: () => void;
  onUpdate: () => void;
  onStartOnboarding?: () => void;
  onNavigate?: (page: ActivePage) => void;
  onOpenAdminPanel?: () => void;
  onOpenSubscription?: () => void;
}

export default function Profile({ profile, onBack, onUpdate, onStartOnboarding, onNavigate, onOpenAdminPanel, onOpenSubscription }: ProfileProps) {
  const { userStats, userRank, userId, loadUserStats, isLoadingStats } = useDataCache();

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showOnboardingDialog, setShowOnboardingDialog] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);

  const stats = userStats || {
    healthScore: 0,
    longevityScore: 0,
    memoryScore: 0,
    energyScore: 0,
    maxStreak: 0,
    totalXP: 0,
    league: 'Bronze',
    totalGames: 0,
  };

  const [formData, setFormData] = useState({
    name: profile.name,
    chronological_age: profile.chronological_age,
    biological_age: profile.biological_age,
    gender: profile.gender,
    country: profile.country,
  });

  useEffect(() => {
    loadUserStats();
  }, [loadUserStats]);

  const handleEditClick = () => {
    if (!profile.onboarding_completed) {
      setShowOnboardingDialog(true);
      return;
    }
    setIsEditing(true);
  };

  const handleCompleteOnboarding = () => {
    setShowOnboardingDialog(false);
    if (onStartOnboarding) {
      onStartOnboarding();
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from('user_profiles')
        .update({
          name: formData.name,
          chronological_age: formData.chronological_age,
          biological_age: formData.biological_age,
          gender: formData.gender,
          country: formData.country,
        })
        .eq('user_id', user.id);

      if (error) throw error;

      setIsEditing(false);
      onUpdate();
      await loadUserStats(true);
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Failed to update profile. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: profile.name,
      chronological_age: profile.chronological_age,
      biological_age: profile.biological_age,
      gender: profile.gender,
      country: profile.country,
    });
    setIsEditing(false);
  };

  // Calculate days left for pro subscription
  const getProDaysLeft = () => {
    if (!profile.subscription_expires_at) return 0;
    const expires = new Date(profile.subscription_expires_at);
    const now = new Date();
    return Math.max(0, Math.ceil((expires.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
  };

  const ageDifference = profile.chronological_age && profile.biological_age
    ? profile.chronological_age - profile.biological_age
    : 0;
  const isYounger = ageDifference > 0;

  const getLeagueBadgeColor = (league: string) => {
    switch (league) {
      case 'Diamond':
        return 'from-cyan-400 to-blue-500';
      case 'Gold':
        return 'from-yellow-400 to-orange-500';
      case 'Silver':
        return 'from-gray-300 to-gray-500';
      default:
        return 'from-amber-600 to-amber-800';
    }
  };

  const getAchievementMilestones = () => {
    const milestones = [3, 7, 14, 30];
    return milestones.map((milestone) => ({
      unlocked: stats.maxStreak >= milestone,
      number: milestone
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <div className="max-w-2xl mx-auto">
        {/* Banner Header */}
        <div className="relative">
          {/* Banner with pattern - Golden for Pro users */}
          <div 
            className="h-32 relative overflow-hidden"
            style={{
              background: profile.is_pro_member
                ? 'linear-gradient(135deg, #92400e, #b45309, #d97706, #f59e0b)'
                : 'linear-gradient(135deg, #22d3ee, #60a5fa, #a78bfa)'
            }}
          >
            {/* Decorative pattern */}
            <div className="absolute inset-0 opacity-20">
              {[...Array(20)].map((_, i) => (
                <div
                  key={i}
                  className="absolute bg-gray-900"
                  style={{
                    width: Math.random() * 30 + 10 + 'px',
                    height: '3px',
                    left: Math.random() * 100 + '%',
                    top: Math.random() * 100 + '%',
                    transform: `rotate(${Math.random() * 360}deg)`,
                  }}
                />
              ))}
            </div>

            {/* Sparkles for Pro users */}
            {profile.is_pro_member && (
              <div className="absolute inset-0 overflow-hidden">
                <Sparkles className="absolute top-4 right-16 w-4 h-4 text-yellow-200/60 animate-pulse" />
                <Sparkles className="absolute top-8 left-16 w-3 h-3 text-yellow-200/40 animate-pulse" style={{ animationDelay: '0.5s' }} />
                <Sparkles className="absolute bottom-4 right-1/4 w-5 h-5 text-yellow-200/50 animate-pulse" style={{ animationDelay: '1s' }} />
              </div>
            )}

            {/* Header buttons */}
            <div className="absolute top-4 left-4 right-4 flex items-center justify-between z-10">
              <button
                onClick={onBack}
                className="w-10 h-10 rounded-xl bg-gray-900 hover:bg-gray-800 flex items-center justify-center transition-colors"
              >
                <ArrowLeft size={20} className="text-white" />
              </button>
              <button className="w-10 h-10 rounded-xl bg-gray-900 hover:bg-gray-800 flex items-center justify-center transition-colors">
                <MoreVertical size={20} className="text-white" />
              </button>
            </div>

            {/* Badge/Rank Label */}
            <div className="absolute top-12 left-1/2 transform -translate-x-1/2">
              <div className={`bg-gradient-to-br ${getLeagueBadgeColor(stats.league)} text-white px-4 py-1 rounded-full font-black text-sm tracking-wider border-4 border-white shadow-lg`}>
                {isLoadingStats ? '...' : stats.league.toUpperCase()}
              </div>
            </div>
          </div>

          {/* Profile Avatar - Overlapping banner */}
          <div className="absolute -bottom-12 left-6">
            <div 
              className="w-24 h-24 rounded-full p-1 border-4 border-white shadow-xl"
              style={{
                background: profile.is_pro_member
                  ? 'linear-gradient(135deg, #fbbf24, #f59e0b)'
                  : 'linear-gradient(135deg, #fb923c, #ef4444)'
              }}
            >
              <div 
                className="w-full h-full rounded-full flex items-center justify-center overflow-hidden"
                style={{
                  background: profile.is_pro_member
                    ? 'linear-gradient(135deg, #fcd34d, #fbbf24)'
                    : 'linear-gradient(135deg, #fdba74, #f87171)'
                }}
              >
                <div 
                  className="w-16 h-16 rounded-full flex items-center justify-center"
                  style={{
                    background: profile.is_pro_member ? '#f59e0b' : '#f97316'
                  }}
                >
                  <span className="text-3xl">{profile.is_pro_member ? '👑' : '😤'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Profile Info Section */}
        <div className="bg-white pt-16 px-6 pb-6 border-b border-gray-200">
          <h2 className="text-2xl font-black text-gray-900 mb-1">{profile.name}</h2>
          <p className="text-sm text-gray-500 mb-4">{profile.email}</p>

          {/* Subscription Status Badge - Replaces "1 Friends" */}
          <div className="flex items-center gap-2 mb-6">
            {profile.is_pro_member ? (
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-amber-400 to-yellow-500 rounded-full">
                  <Crown className="w-4 h-4 text-white" />
                  <span className="text-sm font-bold text-white">PRO MEMBER</span>
                </div>
                <span className="text-xs text-gray-500">
                  {getProDaysLeft()} days left
                </span>
              </div>
            ) : (
              <button
                onClick={onOpenSubscription}
                className="flex items-center gap-2 group"
              >
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 group-hover:bg-gray-200 rounded-full transition-colors">
                  <User className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-bold text-gray-600">FREE PLAN</span>
                </div>
                <span className="text-xs text-cyan-600 font-semibold group-hover:underline">
                  Upgrade →
                </span>
              </button>
            )}
          </div>

          {/* Action Buttons */}
          {!isEditing ? (
            <div className="flex gap-3">
              <button
                onClick={handleEditClick}
                className="flex-1 bg-cyan-500 hover:bg-cyan-600 text-white font-bold py-3 rounded-xl transition-colors flex items-center justify-center gap-2"
              >
                <Edit2 size={18} />
                EDIT PROFILE
              </button>
              <button
                onClick={() => setShowSettings(true)}
                className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold py-3 px-4 rounded-xl transition-colors flex items-center justify-center"
              >
                <SettingsIcon size={18} />
              </button>
            </div>
          ) : (
            <div className="flex gap-3">
              <button
                onClick={handleCancel}
                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold py-3 rounded-xl transition-colors flex items-center justify-center gap-2"
              >
                <X size={18} />
                CANCEL
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="flex-1 bg-green-500 hover:bg-green-600 text-white font-bold py-3 rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <Save size={18} />
                {isSaving ? 'SAVING...' : 'SAVE'}
              </button>
            </div>
          )}
        </div>

        <div className="p-4 space-y-4">
          {!profile.onboarding_completed && onStartOnboarding && (
            <OnboardingBanner onComplete={onStartOnboarding} />
          )}

          {/* Pro Membership Card - Only for Pro users */}
          {profile.is_pro_member && (
            <div 
              className="rounded-2xl p-5 relative overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, #92400e, #b45309, #d97706, #f59e0b)',
              }}
            >
              {/* Sparkles */}
              <Sparkles className="absolute top-3 right-3 w-5 h-5 text-yellow-200/60 animate-pulse" />
              <Sparkles className="absolute bottom-3 left-3 w-4 h-4 text-yellow-200/40 animate-pulse" style={{ animationDelay: '0.5s' }} />
              
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                    <Crown className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-lg">Pro Member</h3>
                    <p className="text-yellow-100/80 text-sm">Premium benefits active</p>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
                    <p className="text-yellow-100 text-xl font-bold">8</p>
                    <p className="text-yellow-200/70 text-[10px]">AI Scans/Day</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
                    <p className="text-yellow-100 text-xl font-bold">∞</p>
                    <p className="text-yellow-200/70 text-[10px]">Protocols</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
                    <p className="text-yellow-100 text-xl font-bold">{getProDaysLeft()}</p>
                    <p className="text-yellow-200/70 text-[10px]">Days Left</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Upgrade Card - Only for Free users */}
          {!profile.is_pro_member && (
            <button
              onClick={onOpenSubscription}
              className="w-full rounded-2xl p-5 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 transition-all text-left"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                    <Sparkles className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-lg">Upgrade to Pro</h3>
                    <p className="text-white/80 text-sm">Get 8 AI scans/day & more</p>
                  </div>
                </div>
                <ChevronRight className="w-6 h-6 text-white" />
              </div>
            </button>
          )}

          {/* Edit Form */}
          {isEditing && (
            <div className="bg-white rounded-2xl p-5 border border-gray-200 space-y-4">
              <h3 className="text-sm font-bold text-gray-900 mb-4">Edit Information</h3>

              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 font-semibold focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-2">
                    Chronological Age
                  </label>
                  <input
                    type="number"
                    value={formData.chronological_age || ''}
                    onChange={(e) => setFormData({ ...formData, chronological_age: Number(e.target.value) })}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 font-semibold focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-2">
                    Biological Age
                  </label>
                  <input
                    type="number"
                    value={formData.biological_age || ''}
                    onChange={(e) => setFormData({ ...formData, biological_age: Number(e.target.value) })}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 font-semibold focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-2">
                    Gender
                  </label>
                  <select
                    value={formData.gender}
                    onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 font-semibold focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  >
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Non-binary">Non-binary</option>
                    <option value="Prefer not to say">Prefer not to say</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-2">
                    Country
                  </label>
                  <input
                    type="text"
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 font-semibold focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Ratings Section */}
          <div>
            <div className="flex items-center justify-between mb-3 px-1">
              <h3 className="text-sm font-bold text-gray-900">Ratings</h3>
              <ChevronRight className="w-4 h-4 text-gray-400" />
            </div>

            {isLoadingStats ? (
              <div className="grid grid-cols-4 gap-3">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="bg-gray-100 rounded-xl h-24 animate-pulse"></div>
                ))}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-4 gap-3">
                  {/* Health */}
                  <div className="bg-white rounded-xl p-3.5 border border-gray-200 text-center">
                    <div className="w-7 h-7 mx-auto mb-2 flex items-center justify-center">
                      <div className="text-green-500 font-black text-sm">▶▶</div>
                    </div>
                    <p className="text-xl font-black text-gray-900 mb-1 leading-none">{stats.healthScore}</p>
                    <p className="text-[9px] font-semibold text-gray-500 uppercase tracking-tight">Health</p>
                  </div>

                  {/* Longevity */}
                  <div className="bg-white rounded-xl p-3.5 border border-gray-200 text-center">
                    <div className="w-7 h-7 mx-auto mb-2 flex items-center justify-center">
                      <Crown className="w-4 h-4 text-cyan-500" />
                    </div>
                    <p className="text-xl font-black text-gray-900 mb-1 leading-none">{stats.longevityScore}</p>
                    <p className="text-[9px] font-semibold text-gray-500 uppercase tracking-tight">Longevity</p>
                  </div>

                  {/* Memory */}
                  <div className="bg-white rounded-xl p-3.5 border border-gray-200 text-center">
                    <div className="w-7 h-7 mx-auto mb-2 flex items-center justify-center">
                      <Target className="w-4 h-4 text-green-500" />
                    </div>
                    <p className="text-xl font-black text-gray-900 mb-1 leading-none">{stats.memoryScore}</p>
                    <p className="text-[9px] font-semibold text-gray-500 uppercase tracking-tight">Memory</p>
                  </div>

                  {/* Energy */}
                  <div className="bg-white rounded-xl p-3.5 border border-gray-200 text-center">
                    <div className="w-7 h-7 mx-auto mb-2 flex items-center justify-center">
                      <Zap className="w-4 h-4 text-purple-500" />
                    </div>
                    <p className="text-xl font-black text-gray-900 mb-1 leading-none">{stats.energyScore}</p>
                    <p className="text-[9px] font-semibold text-gray-500 uppercase tracking-tight">Energy</p>
                  </div>
                </div>

                {/* Score Explanation */}
                <div className="bg-cyan-50 rounded-xl p-4 border border-cyan-200">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-cyan-600 flex-shrink-0 mt-0.5" />
                    <div className="space-y-1.5">
                      <p className="text-xs font-bold text-cyan-900">How Scores Work (0-100 scale):</p>
                      <div className="space-y-1 text-[11px] text-gray-700">
                        <p><span className="font-bold text-green-600">Health:</span> Based on your biological age advantage</p>
                        <p><span className="font-bold text-cyan-600">Longevity:</span> Current streak × 10 points</p>
                        <p><span className="font-bold text-green-600">Memory:</span> Total points from mental maths games</p>
                        <p><span className="font-bold text-purple-600">Energy:</span> Total minutes of yoga & meditation</p>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Stats Overview */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-3 px-1">Stats Overview</h3>

            <div className="grid grid-cols-2 gap-3">
              {/* Max Streak */}
              <div className="bg-white rounded-xl p-4 border border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-3xl font-black text-gray-900 mb-1 leading-none">{stats.maxStreak}</p>
                    <p className="text-[9px] font-semibold text-gray-500 uppercase tracking-tight">Max Streak</p>
                  </div>
                  <div className="bg-orange-100 rounded-lg p-2 border border-orange-200">
                    <Flame className="w-4 h-4 text-orange-500" />
                  </div>
                </div>
              </div>

              {/* Total XP */}
              <div className="bg-white rounded-xl p-4 border border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-3xl font-black text-gray-900 mb-1 leading-none">{stats.totalXP}</p>
                    <p className="text-[9px] font-semibold text-gray-500 uppercase tracking-tight">Total XP</p>
                  </div>
                  <div className="bg-cyan-100 rounded-lg p-2 border border-cyan-200">
                    <Zap className="w-4 h-4 text-cyan-500" />
                  </div>
                </div>
              </div>

              {/* League */}
              <div className="bg-white rounded-xl p-4 border border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-2xl font-black text-gray-900 mb-1 leading-none">{stats.league}</p>
                    <p className="text-[9px] font-semibold text-gray-500 uppercase tracking-tight">League</p>
                  </div>
                  <div className="bg-amber-100 rounded-lg p-2 border border-amber-200">
                    <Medal className="w-4 h-4 text-amber-600" />
                  </div>
                </div>
              </div>

              {/* Total Games */}
              <div className="bg-white rounded-xl p-4 border border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-3xl font-black text-gray-900 mb-1 leading-none">{stats.totalGames}</p>
                    <p className="text-[9px] font-semibold text-gray-500 uppercase tracking-tight">Total Games</p>
                  </div>
                  <div className="bg-blue-100 rounded-lg p-2 border border-blue-200">
                    <Activity className="w-4 h-4 text-blue-500" />
                  </div>
                </div>
              </div>
            </div>

            {/* XP Explanation */}
            <div className="bg-orange-50 rounded-xl p-4 border border-orange-200 mt-3">
              <div className="flex items-start gap-2">
                <Coins className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
                <div className="space-y-1.5">
                  <p className="text-xs font-bold text-orange-900">Earning XP & Streaks:</p>
                  <div className="space-y-1 text-[11px] text-gray-700">
                    <p><span className="font-bold text-orange-600">Food Log:</span> 5 XP per meal logged</p>
                    <p><span className="font-bold text-cyan-600">Activities:</span> 10 XP per yoga/meditation/maths session</p>
                    <p><span className="font-bold text-green-600">Daily Complete:</span> 25 XP when you finish 75%+ of your routine</p>
                    <p><span className="font-bold text-amber-600">Leagues:</span> Bronze (0-6), Silver (7-20), Gold (21-49), Diamond (50+ days)</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Age Analysis */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-3 px-1">Age Analysis</h3>

            {profile.onboarding_completed && profile.chronological_age && profile.biological_age ? (
              <>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div className="text-center bg-white rounded-xl p-4 border border-gray-200">
                    <p className="text-[9px] text-gray-500 uppercase tracking-tight font-semibold mb-2">Chronological</p>
                    <p className="text-3xl font-black text-gray-900">{profile.chronological_age}</p>
                  </div>
                  <div className="text-center bg-white rounded-xl p-4 border-2 border-cyan-400">
                    <p className="text-[9px] text-cyan-600 uppercase tracking-tight font-semibold mb-2">Biological</p>
                    <p className="text-3xl font-black text-cyan-600">{profile.biological_age}</p>
                  </div>
                </div>

                {/* Age Difference */}
                <div className={`bg-white rounded-xl p-5 text-center border-2 ${
                  isYounger
                    ? 'border-green-400'
                    : 'border-orange-400'
                }`}>
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Trophy className={`w-4 h-4 ${isYounger ? 'text-green-600' : 'text-orange-600'}`} />
                    <p className="text-[9px] text-gray-700 font-semibold uppercase tracking-tight">Difference</p>
                  </div>
                  <p className={`text-5xl font-black mb-1 ${
                    isYounger ? 'text-green-600' : 'text-orange-600'
                  }`}>
                    {isYounger ? '-' : '+'}{Math.abs(ageDifference).toFixed(1)}
                  </p>
                  <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">Years</p>
                  <div className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full font-bold text-[10px] ${
                    isYounger
                      ? 'bg-green-500 text-white'
                      : 'bg-orange-500 text-white'
                  }`}>
                    {isYounger ? '🎉 YOUNGER!' : '⚠️ OLDER'}
                  </div>
                </div>
              </>
            ) : (
              <div className="bg-white rounded-xl p-6 border border-gray-200 text-center">
                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
                  <Trophy className="w-6 h-6 text-gray-400" />
                </div>
                <p className="text-sm font-bold text-gray-900 mb-1">Complete Your Onboarding</p>
                <p className="text-xs text-gray-500">Your age analysis will appear here after completing the health assessment.</p>
              </div>
            )}
          </div>

          {/* Achievements */}
          <div>
            <div className="flex items-center justify-between mb-3 px-1">
              <h3 className="text-sm font-bold text-gray-900">Achievements</h3>
              <ChevronRight className="w-4 h-4 text-gray-400" />
            </div>

            <div className="grid grid-cols-4 gap-3">
              {getAchievementMilestones().map((achievement, i) => (
                <div key={i} className={`relative aspect-square rounded-xl border flex flex-col items-center justify-center ${
                  achievement.unlocked 
                    ? 'bg-white border-orange-300' 
                    : 'bg-white border-gray-200'
                }`}>
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-1.5 ${
                    achievement.unlocked 
                      ? 'bg-orange-50' 
                      : 'bg-gray-50'
                  }`}>
                    <Flame className={`w-6 h-6 ${
                      achievement.unlocked ? 'text-orange-500' : 'text-gray-300'
                    }`} />
                  </div>
                  <p className={`text-base font-black ${
                    achievement.unlocked ? 'text-gray-900' : 'text-gray-400'
                  }`}>
                    {achievement.number}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Notifications */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-3 px-1">Notifications</h3>
            <NotificationPrompt />
          </div>

          {/* Leaderboard Section */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-3 px-1">Leaderboard Rankings</h3>
            <div className="bg-white rounded-2xl p-5 border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center">
                    <Trophy className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">Your Global Rank</h4>
                    <p className="text-sm text-gray-500">Based on overall streak</p>
                  </div>
                </div>
              </div>

              {isLoadingStats ? (
                <div className="text-center py-6">
                  <div className="inline-block w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
                  <p className="text-sm text-gray-500 mt-2">Loading rankings...</p>
                </div>
              ) : userRank ? (
                <div className="space-y-4">
                  <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-xl p-4 border-2 border-cyan-300">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-1">Your Rank</p>
                        <p className="text-4xl font-black text-gray-900">#{userRank}</p>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center gap-1.5 text-orange-600 mb-1">
                          <Flame className="w-4 h-4" />
                          <span className="font-bold">{stats.maxStreak} days</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-cyan-600">
                          <Zap className="w-4 h-4" />
                          <span className="font-bold">{stats.totalXP} XP</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => setShowLeaderboard(true)}
                    className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white font-bold py-3 rounded-xl transition-all flex items-center justify-center gap-2"
                  >
                    <Trophy size={18} />
                    View Full Leaderboard
                  </button>
                </div>
              ) : (
                <div className="text-center py-6">
                  <Trophy className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm font-bold text-gray-900 mb-1">Join the Leaderboard</p>
                  <p className="text-xs text-gray-500 mb-4">Complete your daily routine to start competing!</p>
                  <button
                    onClick={() => setShowLeaderboard(true)}
                    className="bg-cyan-500 hover:bg-cyan-600 text-white font-bold py-2 px-4 rounded-xl transition-colors text-sm"
                  >
                    View Leaderboard
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Admin Panel Access - Only visible to admins */}
          {profile.is_admin && (
            <div>
              <h3 className="text-sm font-bold text-gray-900 mb-3 px-1">Admin</h3>
              <button
                onClick={onOpenAdminPanel}
                className="w-full bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-white rounded-2xl p-5 border-2 border-yellow-300 transition-all hover:shadow-lg"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                      <Shield size={24} className="text-white" strokeWidth={2.5} />
                    </div>
                    <div className="text-left">
                      <h4 className="font-bold text-lg mb-0.5">Admin Panel</h4>
                      <p className="text-sm text-white/90">Manage users & notifications</p>
                    </div>
                  </div>
                  <ChevronRight size={24} className="text-white" />
                </div>
              </button>
            </div>
          )}

         {/* Thank You Footer */}
          <div className="bg-white rounded-2xl p-8 border border-gray-200 text-center">
            <div className="flex justify-center mb-4">
              <div className="text-8xl">
                🫰
              </div>
            </div>
            <p className="text-gray-400 text-xs font-bold uppercase tracking-[0.25em]">
              THANK YOU FOR USING THE APP
            </p>
          </div>
        </div>
      </div> 

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-30 safe-area-inset-bottom">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-around">
          <button
            onClick={() => onNavigate ? onNavigate('home') : onBack()}
            className="flex flex-col items-center gap-1 text-gray-400 hover:text-gray-600 transition-colors min-w-[60px]"
          >
            <Home size={22} strokeWidth={2} />
            <span className="text-[10px] font-bold">Home</span>
          </button>
          <button
            onClick={() => onNavigate && onNavigate('progress')}
            className="flex flex-col items-center gap-1 text-gray-400 hover:text-gray-600 transition-colors min-w-[60px]"
          >
            <TrendingUp size={22} strokeWidth={2} />
            <span className="text-[10px] font-bold">Progress</span>
          </button>
          <button
            onClick={() => onNavigate && onNavigate('activity')}
            className="flex flex-col items-center gap-1 text-gray-400 hover:text-gray-600 transition-colors min-w-[60px]"
          >
            <Activity size={22} strokeWidth={2} />
            <span className="text-[10px] font-bold">Activity</span>
          </button>
          <button className="flex flex-col items-center gap-1 text-cyan-600 min-w-[60px] relative">
            <div className="absolute -top-1 w-12 h-0.5 bg-cyan-600 rounded-full"></div>
            <User size={22} strokeWidth={2.5} />
            <span className="text-[10px] font-bold">Profile</span>
          </button>
        </div>
      </nav>

      {showOnboardingDialog && (
        <CompleteProfileModal
          onComplete={handleCompleteOnboarding}
          onClose={() => setShowOnboardingDialog(false)}
        />
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-scaleIn {
          animation: scaleIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(100%);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slideUp {
          animation: slideUp 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        .pb-safe {
          padding-bottom: max(1.5rem, env(safe-area-inset-bottom));
        }

        .safe-area-inset-bottom {
          padding-bottom: max(0.75rem, env(safe-area-inset-bottom));
        }

        .touch-manipulation {
          touch-action: manipulation;
          -webkit-tap-highlight-color: transparent;
        }
      `}</style>

      {/* Settings Page */}
      {showSettings && <Settings onClose={() => setShowSettings(false)} />}

      {/* Leaderboard Modal */}
      {showLeaderboard && (
        <LeaderboardModal
          onClose={() => setShowLeaderboard(false)}
          currentUserId={userId}
        />
      )}
    </div>
  );
}